// <copyright file="DleRecordType.tsx">
// © 2024 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

/* eslint-disable @typescript-eslint/no-explicit-any */

import axios from "axios";
import { Dropdown } from "primereact/dropdown";
import { Menubar } from "primereact/menubar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useState as _useState, useContext, useEffect } from "react";
import styles from "src/components/DataLakeExplorer/DleRecordType.module.css";
import DleRecordTypeDataSource from "src/components/DataLakeExplorer/DleRecordTypeDataSource";
import DleRecordTypeDetail from "src/components/DataLakeExplorer/DleRecordTypeDetail";
import DleRecordTypeQuery from "src/components/DataLakeExplorer/DleRecordTypeQuery";
import DleRecordTypeSchema from "src/components/DataLakeExplorer/DleRecordTypeSchema";
import ConfigContext from "src/context/ConfigContext";
import {
    LoadingStatus,
    displayLoadingError,
    getCookieJsonField,
    sortDleSchemaVersions,
} from "src/utils/SharedUtils";
import type { AxiosRequestConfig } from "axios";
import type { MenuItem } from "primereact/menuitem";
import type { FC } from "react";
import type { RecordType } from "src/components/DataLakeExplorer/DataLakeExplorer";
import type { IConfig } from "src/utils/ConfigLoader";

// Properties to initialize this component
interface DleRecordTypeProps {
    useState?: typeof _useState; // optional hook override (for tests)
    recordType: string;
    allRecordTypes: RecordType[];
}

// API data model - MUST match API response for GET /dle-schema-versions
interface SchemaVersionsResponse {
    SchemaVersions: SchemaVersion[];
}
export interface SchemaVersion {
    TableName: string;
    SchemaName: string;
    MetadataVersion: string;
    IsLatest: boolean;
}

// API data model - MUST match API response for GET /dle-schema-version-details
interface SchemaVersionDetailsResponse {
    SchemaVersionDetail: SchemaVersionDetail;
}
export interface SchemaVersionDetail {
    SchemaName: string;
    MetadataVersion: string;
    IsLatest: boolean;
    Description: string | null; // Added after db fetch
    SchemaCreateDate: string;
    StartDate: string | null;
    EndDate: string | null;
    IsOngoing: boolean;
}

// API data model - MUST match API response for GET /dle-schema-version-fields
interface SchemaVersionFieldsResponse {
    SchemaFields: SchemaVersionField[];
}
export interface SchemaVersionField {
    SchemaName: string;
    SchemaVersion: string;
    FieldIndex: string;
    FieldName: string | null;
    FieldType: string | null;
    FieldDoc: string | null;
}

// API data model - MUST match API response for GET /dle-schema-version-data-sources
interface SchemaVersionDataSourcesResponse {
    DleDataSourceInfo: DataSource[];
}
export interface DataSource {
    Agency: string;
    ProducerName: string;
    HostType: string;
    RawType: string;
    IngestionMethod: string;
    IngestionFrequency: string;
    SchemaCreated: string;
    PointOfContact: string;
    RecordType: string;
    PartitionKeyFormat: string;
    PartitionKeyExample: string;
}

// Info/error message popup
const toast = React.createRef<Toast>();

// Menu items for content display
const viewMenuItems: MenuItem[] = [
    { label: "Record Type Details" },
    { label: "Schema Details" },
    { label: "Sample Queries" },
    { label: "Data Sources" },
];

/**
 * Determines whether to display a progress spinner to indicate data loading, based on the
 * status of data loads and current user view selection
 * @param loadingStatus Status of data load
 * @returns Boolean indicator whether loading spinner should be shown
 */
function shouldDisplayLoadingSpinner(loadingStatus: LoadingStatus) {
    return loadingStatus === LoadingStatus.loading;
}

/**
 * Displays a selected sub-view menu item, e.g., schema version details or tabular schema
 * @param selectedItemLabel Label of selected sib-view menu item or undefined
 * @param setMenuSelected Function that sets the selection state
 */
function displaySelectedMenuItem(
    selectedItemLabel: string | undefined,
    setMenuSelected: React.Dispatch<React.SetStateAction<string | undefined>>
) {
    setMenuSelected(selectedItemLabel);
}

/**
 * Determines whether to display a selected sub-view menu item, e.g., schema version details or tabular schema
 * @param label Label of sub-view menu item to display
 * @param menuSelected Label of selected sub-view menu item or undefined
 * @returns True if sub-view item should display, else false
 */
function shouldDisplayView(label: string, menuSelected: string | undefined) {
    return menuSelected && menuSelected === label;
}

/**
 * Retrieves schema versions from database API
 * @param configData Application configuration
 * @param props Component properties
 * @param setLoadingStatus Function that sets the loading status state
 */
async function fetchSchemaVersions(
    configData: IConfig,
    props: DleRecordTypeProps,
    setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>>
): Promise<SchemaVersion[]> {
    console.debug(`Fetching DLE schema versions for ${props.recordType}...`);
    const svResponse: SchemaVersionsResponse = {
        SchemaVersions: [],
    };
    const config: AxiosRequestConfig<any> = {
        params: {
            schemaName: props.recordType,
        },
        headers: {
            Authorization: `Bearer ${getCookieJsonField(
                "DepSessionIdentity",
                "token"
            )}`,
        },
    };
    await axios
        .get<SchemaVersionsResponse>(
            `${configData.apiBaseUrl}/dle-schema-versions`,
            config
        )
        .then((response) => {
            console.debug(response.data);
            svResponse.SchemaVersions = response.data.SchemaVersions;
        })
        .catch((error) => {
            const summary = "Error fetching DLE schema versions";
            const detail = error.response
                ? error.response.data.errorMessage
                : error.message;
            displayLoadingError(
                summary,
                detail,
                toast.current,
                setLoadingStatus
            );
        });
    // Sort results with most recent version first
    return svResponse.SchemaVersions.sort((aa, bb) =>
        bb.MetadataVersion.localeCompare(aa.MetadataVersion)
    );
}

/**
 * Retrieves schema version details from database API
 * @param configData Application configuration
 * @param setLoadingStatus Function that sets the loading status state
 * @param schemaVersion The schema version to retrieve
 */
async function fetchSchemaVersionDetail(
    configData: IConfig,
    setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>>,
    schemaVersion: SchemaVersion
): Promise<SchemaVersionDetail | undefined> {
    console.debug(
        `Fetching DLE schema version details for ${schemaVersion.SchemaName}/${schemaVersion.MetadataVersion}...`
    );
    let svDetail: SchemaVersionDetail | undefined;
    const config: AxiosRequestConfig<any> = {
        params: {
            schemaName: schemaVersion.SchemaName,
            schemaVersion: schemaVersion.MetadataVersion,
        },
        headers: {
            Authorization: `Bearer ${getCookieJsonField(
                "DepSessionIdentity",
                "token"
            )}`,
        },
    };
    await axios
        .get<SchemaVersionDetailsResponse>(
            `${configData.apiBaseUrl}/dle-schema-version-details`,
            config
        )
        .then((response) => {
            console.debug(response.data);
            svDetail = { ...response.data.SchemaVersionDetail };
        })
        .catch((error) => {
            const summary = "Error fetching DLE schema version details";
            const detail = error.response
                ? error.response.data.errorMessage
                : error.message;
            displayLoadingError(
                summary,
                detail,
                toast.current,
                setLoadingStatus
            );
        });
    return svDetail;
}

async function fetchDataSourceDetails(
    configData: IConfig,
    setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>>,
    schemaVersion: SchemaVersion,
): Promise<DataSource[]> {
    console.debug(
        `Fetching DLE data source info for ${schemaVersion.SchemaName}/${schemaVersion.MetadataVersion}...`,
    );
    let dataSourceDetails: DataSource[] = [];
    const config: AxiosRequestConfig<any> = {
        params: {
            schemaName: schemaVersion.SchemaName,
            schemaVersion: schemaVersion.MetadataVersion
        },
        headers: {
            Authorization: `Bearer ${getCookieJsonField(
                "DepSessionIdentity",
                "token",
            )}`,
        },
    };
    return await new Promise((resolve, reject) =>{
        axios
        .get<SchemaVersionDataSourcesResponse>(
            `${configData.apiBaseUrl}/dle-schema-version-data-sources`,
            config,
        )
        .then((response) => {
            console.debug(response.data);
            dataSourceDetails = response.data.DleDataSourceInfo;
            resolve(dataSourceDetails)
        })
        .catch((error) => {
            const summary = "Error fetching DLE data source info";
            const detail = error.response
                ? error.response.data.errorMessage
                : error.message;
            displayLoadingError(summary, detail, toast.current, setLoadingStatus);
            reject({summary: summary, detail: detail})
        });
    })
}

/**
 * Retrieves schema version tabular field info from database API
 * @param configData Application configuration
 * @param setLoadingStatus Function that sets the loading status state
 * @param schemaVersion The schema version to retrieve
 */
async function fetchTabularSchema(
    configData: IConfig,
    setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>>,
    schemaVersion: SchemaVersion
): Promise<SchemaVersionField[]> {
    console.debug(
        `Fetching DLE tabular schema for ${schemaVersion.SchemaName}/${schemaVersion.MetadataVersion}...`
    );
    let schema: SchemaVersionField[] = [];
    const config: AxiosRequestConfig<any> = {
        params: {
            schemaName: schemaVersion.SchemaName,
            schemaVersion: schemaVersion.MetadataVersion,
        },
        headers: {
            Authorization: `Bearer ${getCookieJsonField(
                "DepSessionIdentity",
                "token"
            )}`,
        },
    };
    await axios
        .get<SchemaVersionFieldsResponse>(
            `${configData.apiBaseUrl}/dle-schema-version-fields`,
            config
        )
        .then((response) => {
            console.debug(response.data);
            schema = response.data.SchemaFields;
        })
        .catch((error) => {
            const summary = "Error fetching DLE tabular schema";
            const detail = error.response
                ? error.response.data.errorMessage
                : error.message;
            displayLoadingError(
                summary,
                detail,
                toast.current,
                setLoadingStatus
            );
        });
    return schema;
}

/**
 * Creates a schema version drop down item
 * @param schemaVersion Schema version info
 * @returns JSX/web component for the drop down item
 */
function dropdownLabelTemplate(schemaVersion: SchemaVersion) {
    if (schemaVersion) {
        return (
            <div>
                {schemaVersion.SchemaName} {schemaVersion.MetadataVersion}{" "}
                {schemaVersion.IsLatest ? "(latest)" : ""}
            </div>
        );
    }

    return <div>Select a version</div>;
}

/**
 * DLE record type component
 * @param props Component props
 * @returns The component
 */
const DleRecordType: FC<DleRecordTypeProps> = (props: DleRecordTypeProps) => {
    // Initialize state & handlers
    const configData = useContext(ConfigContext) as IConfig;
    const useState = props.useState ?? _useState;
    const [viewSelected, setViewSelected] = useState<string | undefined>();
    const [versionSelected, setVersionSelected] = useState<
        SchemaVersion | undefined
    >();
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.preload);
    const [dropdownSchemaVersions, setDropdownSchemaVersions] = useState<
        SchemaVersion[]
    >([]);
    const [allSchemaVersions, setAllSchemaVersions] = useState<SchemaVersion[]>(
        []
    );
    const [schemaVersionDetail, setSchemaVersionDetail] = useState<
        SchemaVersionDetail | undefined
    >();
    const [tabularSchema, setTabularSchema] = useState<SchemaVersionField[]>(
        []
    );
    const [dataSourceDetail, setDataSourceDetail] = useState<DataSource[]>(
        []
    );

    // Set menu actions
    viewMenuItems
        .filter((it) => it.label !== undefined)
        .forEach((it) => {
            it.command = () => {
                displaySelectedMenuItem(it.label, setViewSelected);
            };
        });

    // Hook on record type state change to retrieve data
    // useEffect should be used SPARINGLY due to CASCADE potential
    useEffect(() => {
        const fetchData = async () => {
            // Set initial states
            setLoadingStatus(LoadingStatus.loading);
            setVersionSelected(undefined);
            setAllSchemaVersions([]);
            setDropdownSchemaVersions([]);
            setViewSelected(undefined);
            setDataSourceDetail([]);
            setTabularSchema([]);
            setSchemaVersionDetail(undefined);

            // Fetch data
            const allVersions: SchemaVersion[] = await fetchSchemaVersions(
                configData,
                props,
                setLoadingStatus
            );
            const dropdownVersions = allVersions.reduce(
                (accumulator: SchemaVersion[], current: SchemaVersion) =>
                    // Load drop-down options, group by version and take first
                    accumulator.some(
                        (x) => x.MetadataVersion == current.MetadataVersion
                    )
                        ? accumulator
                        : [...accumulator, current],
                []
            );
            console.debug(
                `DleRecordType ${props.recordType} fetched ${dropdownVersions.length}`
            );
            let defaultSelection: SchemaVersion | undefined =
                dropdownVersions.find((it) => it.IsLatest);
            if (!defaultSelection) {
                console.warn(
                    `DleRecordType ${props.recordType} could not find latest version of schema`
                );
                defaultSelection = dropdownVersions[0]; // Assumes options are sorted, most recent first
            }

            // Set final states
            setDropdownSchemaVersions(dropdownVersions);
            setAllSchemaVersions(allVersions);
            setVersionSelected(defaultSelection); // CASCADES useEffect
            setLoadingStatus(LoadingStatus.loaded);
        };

        fetchData();
    }, [props.recordType]);

    // Hook on version selection state changes to retrieve data
    // useEffect should be used SPARINGLY due to CASCADE potential
    useEffect(() => {
        const fetchData = async () => {
            // Set initial states
            setLoadingStatus(LoadingStatus.loading);
            setViewSelected(undefined);
            setSchemaVersionDetail(undefined);
            console.debug(
                `DleRecordType versionSelected: ${props.recordType} ${versionSelected?.MetadataVersion}`
            );
            if (!versionSelected) {
                return;
            }

            // Fetch data
            const selectedDetails: SchemaVersionDetail | undefined =
                await fetchSchemaVersionDetail(
                    configData,
                    setLoadingStatus,
                    versionSelected
                );
            const selectedSchema: SchemaVersionField[] =
                await fetchTabularSchema(
                    configData,
                    setLoadingStatus,
                    versionSelected
                );
            if (selectedDetails) {
                // Assuming field "1" is metadata and "2" is a specific record type,
                // set description using the doc field for the record type
                selectedDetails.Description =
                    selectedSchema.find((ts) => ts.FieldIndex === "2")
                        ?.FieldDoc || "unknown";
            }
            const dataSourceInfo: DataSource[] =
                await fetchDataSourceDetails(
                    configData,
                    setLoadingStatus,
                    versionSelected
                );

            // Set final states
            setSchemaVersionDetail(selectedDetails);
            setTabularSchema(selectedSchema);
            setDataSourceDetail(dataSourceInfo);
            setViewSelected("Record Type Details");
            setLoadingStatus(LoadingStatus.loaded);
        };

        fetchData();
    }, [versionSelected]);

    // Create components
    return (
        <div className={styles.DleRecordType} data-testid="DleRecordType">
            <Toast ref={toast} />
            {shouldDisplayLoadingSpinner(loadingStatus) && (
                <ProgressSpinner className="App-spinner" aria-label="Loading" />
            )}
            {!shouldDisplayLoadingSpinner(loadingStatus) && (
                <div className="flex flex-row align-items-center">
                    <label htmlFor="select-schema-version" className="mr-2">
                        Schema Version:
                    </label>
                    <Dropdown
                        autoFocus={true}
                        id="select-schema-version"
                        title="select-schema-version"
                        className="px-0"
                        value={versionSelected}
                        onChange={(e) => setVersionSelected(e.value)}
                        options={dropdownSchemaVersions.sort((aa, bb) => sortDleSchemaVersions(aa, bb))}
                        optionLabel="metadataVersion"
                        itemTemplate={dropdownLabelTemplate}
                        valueTemplate={dropdownLabelTemplate}
                    />
                </div>
            )}
            {!shouldDisplayLoadingSpinner(loadingStatus) && (
                <Menubar className={styles.MenuBar} model={viewMenuItems} />
            )}
            {!shouldDisplayLoadingSpinner(loadingStatus) &&
                shouldDisplayView("Record Type Details", viewSelected) && (
                    <DleRecordTypeDetail
                        recordType={props.recordType}
                        allRecordTypes={props.allRecordTypes}
                        allSchemVersions={allSchemaVersions}
                        schemaVersionDetail={schemaVersionDetail}
                    />
                )}
            {!shouldDisplayLoadingSpinner(loadingStatus) &&
                shouldDisplayView("Schema Details", viewSelected) && (
                    <DleRecordTypeSchema
                        recordType={props.recordType}
                        schemaVersion={versionSelected}
                        tabularSchema={tabularSchema}
                    />
                )}
            {!shouldDisplayLoadingSpinner(loadingStatus) &&
                shouldDisplayView("Sample Queries", viewSelected) && (
                    <DleRecordTypeQuery recordType={props.recordType} />
                )}
            {!shouldDisplayLoadingSpinner(loadingStatus) &&
                shouldDisplayView("Data Sources", viewSelected) && (
                    <DleRecordTypeDataSource recordType={props.recordType}
                    dataInfo={dataSourceDetail} />
                )}
        </div>
    );
};

export default DleRecordType;
